import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  InputPesquisaFacial,
  RespostaReconhecimentoFacial,
} from "../types/mandado-facial.types";
import { consultarReconhecimentoFacial } from "../services/reconhecimento-facial.service";

/**
 * Hook customizado para gerenciar lógica de reconhecimento facial
 *
 * @param cpfUsuario - CPF do usuário autenticado
 * @param latitude - Latitude opcional para auditoria
 * @param longitude - Longitude opcional para auditoria
 */
export const useMandadoFacial = (
  cpfUsuario: string,
  latitude?: string,
  longitude?: string
) => {
  const { control, handleSubmit, setValue, watch } =
    useForm<InputPesquisaFacial>({
      defaultValues: {
        foto: null,
      },
    });

  const [loading, setLoading] = useState(false);
  const [showFaceCapture, setShowFaceCapture] = useState(false);
  const [resultadoReconhecimento, setResultadoReconhecimento] =
    useState<RespostaReconhecimentoFacial | null>(null);
  const [mensagemMandado, setMensagemMandado] = useState<string | null>(null);
  const [fotoEnviada, setFotoEnviada] = useState<string | null>(null);

  const limparConsulta = () => {
    setResultadoReconhecimento(null);
    setMensagemMandado(null);
    setFotoEnviada(null);
    setValue("foto", null);
  };

  const handleFaceCaptured = (capturedFile: File) => {
    setValue("foto", capturedFile);
    setShowFaceCapture(false);
    console.log("Foto capturada:", capturedFile);
  };

  const pesquisarReconhecimentoFacial = async (data: InputPesquisaFacial) => {
    console.log("Iniciando pesquisa facial com dados:", data);
    if (!data.foto) {
      setMensagemMandado("Por favor, selecione uma foto para análise.");
      return;
    }

    setLoading(true);
    setMensagemMandado(null);
    setResultadoReconhecimento(null);

    try {
      const resultado = await consultarReconhecimentoFacial(
        cpfUsuario,
        data.foto,
        latitude,
        longitude
      );

      // Salvar foto enviada para comparação
      const reader = new FileReader();
      reader.readAsDataURL(data.foto);
      reader.onload = () => {
        setFotoEnviada(reader.result as string);
      };

      if (resultado && resultado.pessoas && resultado.pessoas.length > 0) {
        setResultadoReconhecimento(resultado);
        setMensagemMandado(null);
        console.log(
          "✅ Reconhecimento realizado:",
          resultado.pessoas.length,
          "pessoa(s)"
        );
      } else {
        setMensagemMandado(resultado.msg || "Nenhuma pessoa reconhecida.");
        setResultadoReconhecimento(null);
      }
    } catch (error: any) {
      console.error("❌ Erro:", error);
      setMensagemMandado(
        error.message || "Erro inesperado ao consultar mandado."
      );
    } finally {
      setLoading(false);
    }
  };

  return {
    control,
    handleSubmit,
    setValue,
    watch,
    loading,
    showFaceCapture,
    setShowFaceCapture,
    resultadoReconhecimento,
    mensagemMandado,
    fotoEnviada,
    limparConsulta,
    handleFaceCaptured,
    pesquisarReconhecimentoFacial,
  };
};
